
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const ChatbotsPage = () => {
  const [activeTab, setActiveTab] = useState('features');

  const features = [
    {
      icon: 'ri-message-3-line',
      title: '24/7 Client Support',
      description: 'Never miss a lead with round-the-clock automated responses to client inquiries.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Lead Qualification',
      description: 'Automatically qualify leads based on budget, timeline, and property preferences.'
    },
    {
      icon: 'ri-calendar-line',
      title: 'Appointment Scheduling',
      description: 'Seamlessly schedule property viewings and consultations directly through chat.'
    },
    {
      icon: 'ri-translate-2',
      title: 'Multi-language Support',
      description: 'Communicate with clients in their preferred language for better engagement.'
    },
    {
      icon: 'ri-database-2-line',
      title: 'CRM Integration',
      description: 'Automatically sync lead data with your existing CRM systems.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile Optimized',
      description: 'Works perfectly on all devices including smartphones and tablets.'
    }
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '$199',
      period: '/month',
      description: 'Perfect for individual agents',
      features: [
        'Up to 1,000 conversations/month',
        'Basic lead qualification',
        'Email integration',
        'Standard templates',
        'Basic analytics'
      ],
      popular: false
    },
    {
      name: 'Professional',
      price: '$399',
      period: '/month',
      description: 'Ideal for growing teams',
      features: [
        'Up to 5,000 conversations/month',
        'Advanced lead qualification',
        'CRM integration',
        'Custom templates',
        'Advanced analytics',
        'Multi-language support'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: '$799',
      period: '/month',
      description: 'For large brokerages',
      features: [
        'Unlimited conversations',
        'AI-powered insights',
        'Custom integrations',
        'White-label solution',
        'Priority support',
        'Custom training'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">
                AI Real Estate Chatbots
              </h1>
              <p className="text-xl mb-8">
                Transform your client communication with intelligent chatbots that work 24/7 to capture leads, 
                qualify prospects, and schedule appointments automatically.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/demo" 
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Try Live Demo
                </Link>
                <Link 
                  to="/contact" 
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Get Started
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/4be71407095666198ef238fbeba3d241.jpg"
                alt="AI Chatbot Interface"
                className="w-full rounded-lg shadow-2xl object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'features', label: 'Features' },
              { id: 'demo', label: 'Live Demo' },
              { id: 'pricing', label: 'Pricing' },
              { id: 'integration', label: 'Integration' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Features Tab */}
      {activeTab === 'features' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Powerful Features for Real Estate Success</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our AI chatbots are specifically designed for real estate professionals with features that drive results.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Demo Tab */}
      {activeTab === 'demo' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">See Our Chatbot in Action</h2>
              <p className="text-xl text-gray-600">
                Experience how our AI chatbot handles real estate inquiries and qualifies leads.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Interactive Demo</h3>
                  <div className="bg-gray-100 rounded-lg p-6 h-96 flex items-center justify-center">
                    <div className="text-center">
                      <i className="ri-message-3-line text-6xl text-blue-600 mb-4"></i>
                      <p className="text-gray-600 mb-4">Live chatbot demo will be embedded here</p>
                      <Link 
                        to="/demo" 
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Launch Full Demo
                      </Link>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Sample Conversations</h3>
                  <div className="space-y-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-medium text-blue-900">Client:</p>
                      <p className="text-blue-800">"I'm looking for a 3-bedroom house under $500k"</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium text-gray-900">AI Bot:</p>
                      <p className="text-gray-800">"Great! I can help you find the perfect home. What's your preferred location and timeline?"</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-medium text-blue-900">Client:</p>
                      <p className="text-blue-800">"Downtown area, looking to move in 3 months"</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium text-gray-900">AI Bot:</p>
                      <p className="text-gray-800">"Perfect! I found 12 properties matching your criteria. Would you like to schedule a viewing this week?"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Pricing Tab */}
      {activeTab === 'pricing' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Plan</h2>
              <p className="text-xl text-gray-600">
                Flexible pricing options to fit your business needs and budget.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {pricingPlans.map((plan, index) => (
                <div key={index} className={`bg-white rounded-xl shadow-lg p-8 relative ${plan.popular ? 'ring-2 ring-blue-500' : ''}`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-600 ml-1">{plan.period}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <i className="ri-check-line text-green-500 mr-3"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    to="/contact"
                    className={`w-full py-3 px-6 rounded-lg font-semibold text-center block transition-colors whitespace-nowrap cursor-pointer ${
                      plan.popular 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Integration Tab */}
      {activeTab === 'integration' && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Seamless Integrations</h2>
              <p className="text-xl text-gray-600">
                Connect with your existing tools and workflows for maximum efficiency.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {[
                { name: 'Salesforce', logo: 'ri-salesforce-line' },
                { name: 'HubSpot', logo: 'ri-customer-service-2-line' },
                { name: 'Zillow', logo: 'ri-home-4-line' },
                { name: 'MLS', logo: 'ri-database-2-line' },
                { name: 'Gmail', logo: 'ri-mail-line' },
                { name: 'Slack', logo: 'ri-slack-line' },
                { name: 'Zapier', logo: 'ri-links-line' },
                { name: 'WordPress', logo: 'ri-wordpress-line' }
              ].map((integration, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
                  <i className={`${integration.logo} text-4xl text-blue-600 mb-3`}></i>
                  <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                </div>
              ))}
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-semibold text-center mb-8">Easy Setup Process</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-600">1</span>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Connect Your Tools</h4>
                  <p className="text-gray-600">Link your existing CRM, email, and website platforms with our simple integration wizard.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-600">2</span>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Customize Responses</h4>
                  <p className="text-gray-600">Train the AI with your specific property information and communication style.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-blue-600">3</span>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Go Live</h4>
                  <p className="text-gray-600">Deploy your chatbot and start capturing leads 24/7 across all your channels.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ChatbotsPage;
